import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Camera, ArrowLeft, Users, User } from "lucide-react";
import { photoUrl } from "../../lib/photos";
import { BRAND, errMsg } from "./constants";

const POLL_MS = 3000;
const ROLE_COLOR = { owner: BRAND.orange, manager: BRAND.green, guest: "#2563EB" };

function ThreadList({ threads, active, onPick, me }) {
  return (
    <div data-testid="chat-thread-list" className="divide-y overflow-y-auto max-h-[60vh]">
      {threads.length === 0 && <div className="p-4 text-xs" style={{ color: BRAND.muted }}>No chats yet.</div>}
      {threads.map((t) => (
        <button key={t.id} data-testid={`chat-thread-${t.kind}-${t.id}`} onClick={() => onPick(t)}
          className="w-full text-left p-3 flex items-center gap-3 transition-colors hover:bg-emerald-50"
          style={{ background: active === t.id ? "#ECFDF5" : "transparent" }}>
          <span className="w-9 h-9 rounded-full grid place-items-center text-white shrink-0" style={{ background: t.kind === "team" ? BRAND.green : BRAND.orange }}>
            {t.kind === "team" ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-bold text-sm truncate" style={{ color: BRAND.green }}>{me === "guest" ? t.property_name : t.title}</span>
            <span className="block text-xs truncate" style={{ color: BRAND.muted }}>{t.last_preview || "Say hello 👋"}</span>
          </span>
          {t.unread > 0 && <span data-testid={`chat-unread-${t.id}`} className="bed-blink rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: BRAND.orange }}>{t.unread}</span>}
        </button>
      ))}
    </div>
  );
}

function Bubble({ m, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className="max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm" style={{ background: mine ? BRAND.green : "#fff", color: mine ? "#fff" : "#111", borderBottomRightRadius: mine ? 4 : 16, borderBottomLeftRadius: mine ? 16 : 4 }}>
        {!mine && <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: ROLE_COLOR[m.sender_role] }}>{m.sender_name} · {m.sender_role}</div>}
        {m.photo && <a href={photoUrl(m.photo)} target="_blank" rel="noreferrer"><img src={photoUrl(m.photo)} alt="attachment" className="mt-1 rounded-xl max-h-56 object-cover" /></a>}
        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
        <div className="text-[10px] mt-0.5 opacity-70 text-right">{new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
    </div>
  );
}

function Conversation({ thread, me, userId, authFetch, API, onBack, onActivity }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const lastRef = useRef(null);

  const fetchNew = useCallback(async () => {
    const after = lastRef.current ? `?after=${encodeURIComponent(lastRef.current)}` : "";
    const r = await authFetch({ url: `${API}/homestay/threads/${thread.id}/messages${after}` });
    if (r.data.items.length) {
      lastRef.current = r.data.items[r.data.items.length - 1].created_at;
      setMsgs((prev) => [...prev, ...r.data.items.filter((m) => !prev.some((p) => p.id === m.id))]);
      onActivity();
    }
  }, [thread.id, authFetch, API, onActivity]);

  useEffect(() => {
    setMsgs([]); lastRef.current = null;
    fetchNew().catch(() => {});
    const t = setInterval(() => fetchNew().catch(() => {}), POLL_MS);
    return () => clearInterval(t);
  }, [fetchNew]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (payload) => {
    setBusy(true);
    try { await authFetch({ method: "post", url: `${API}/homestay/threads/${thread.id}/messages`, data: payload }); setText(""); await fetchNew(); }
    catch (e) { toast.error(errMsg(e, "Message not sent")); }
    finally { setBusy(false); }
  };
  const sendPhoto = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await authFetch({ method: "post", url: `${API}/upload/listing-photo`, data: form, headers: { "Content-Type": "multipart/form-data" } });
      await send({ photo: r.data.path, text: text || null });
    } catch (e) { toast.error(errMsg(e, "Photo upload failed")); }
  };

  return (
    <div className="flex flex-col h-[60vh]" data-testid="chat-conversation">
      <div className="flex items-center gap-2 p-3 border-b" style={{ borderColor: BRAND.line }}>
        {onBack && <button data-testid="chat-back-btn" onClick={onBack} className="md:hidden"><ArrowLeft className="w-4 h-4" /></button>}
        <div className="font-bold text-sm" style={{ color: BRAND.green }} data-testid="chat-title">{me === "guest" ? thread.property_name : thread.title}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: "#F6F8F7" }} data-testid="chat-messages">
        {msgs.length === 0 && <div className="text-xs text-center pt-8" style={{ color: BRAND.muted }}>No messages yet.</div>}
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === userId} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send({ text }); }} className="flex items-center gap-2 p-2 border-t" style={{ borderColor: BRAND.line }}>
        <label data-testid="chat-photo-btn" className="p-2 rounded-full cursor-pointer hover:bg-emerald-50">
          <Camera className="w-5 h-5" style={{ color: BRAND.green }} />
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && sendPhoto(e.target.files[0])} />
        </label>
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 rounded-full border-2 px-4 py-2 text-sm outline-none focus:border-[#00604A]" style={{ borderColor: BRAND.line }} />
        <button disabled={busy || !text.trim()} data-testid="chat-send-btn" className="p-2.5 rounded-full text-white disabled:opacity-50" style={{ background: BRAND.orange }}><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

export function ChatPanel({ threadsUrl, me, userId, authFetch, API, initialThreadId }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const loadThreads = useCallback(() => authFetch({ url: threadsUrl }).then((r) => {
    setThreads(r.data.items);
    setActive((cur) => cur || r.data.items.find((t) => t.id === initialThreadId) || (window.innerWidth >= 768 ? r.data.items[0] : null) || null);
  }).catch(() => {}), [threadsUrl, authFetch, initialThreadId]);
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 10000); return () => clearInterval(t); }, [loadThreads]);

  return (
    <div data-testid="chat-panel" className="rounded-2xl border-2 bg-white overflow-hidden grid md:grid-cols-[18rem_1fr]" style={{ borderColor: BRAND.line }}>
      <div className={`${active ? "hidden md:block" : ""} border-r`} style={{ borderColor: BRAND.line }}>
        <ThreadList threads={threads} active={active?.id} onPick={setActive} me={me} />
      </div>
      <div className={active ? "" : "hidden md:block"}>
        {active ? <Conversation thread={active} me={me} userId={userId} authFetch={authFetch} API={API} onBack={() => setActive(null)} onActivity={loadThreads} />
          : <div className="h-[60vh] grid place-items-center text-sm" style={{ color: BRAND.muted }}>Pick a chat</div>}
      </div>
    </div>
  );
}
